import React, { useState ,useEffect} from 'react';
import {
  Paper, Typography, IconButton, Menu, MenuItem, CardActions, Box
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { editProject, deleteProject as deleteProjectApi, fetchUserDetails } from '../DatabaseApis/DBQuery'; // Adjust the path as necessary
import { getCookie } from '../utils/cookieUtils';
// import {jwtDecode} from 'jwt-decode';
// ProjectCard component displays project details and allows editing and deleting by admins
const ProjectCard = ({ project, onEdit, onDelete }) => {
  // State to manage the anchor element for the options menu
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate(); // Hook to programmatically navigate
  const [userRole, setRole] = useState('');
  // Fetch user data from session storage to check if the user is an Admin
  const userData = getCookie('userData');
  // const decodedToken = jwtDecode(userData.token);
  const isAdmin = userData && (userRole === 'Admin' || userRole === 'Tenant Admin'); // Determine if the user has Admin role


  useEffect(() => {
        const getUserDetails = async () => {
            // const userData = JSON.parse(sessionStorage.getItem('userData'));
            const token = userData?.token;
    
            if (!token) {
              console.error('Token not found in session storage');
              return;
            }
            try {
              const data = await fetchUserDetails(token);
              // console.log(data);
              // console.log(data.email);
              // console.log(data.tenantId);
              // console.log(data.role);
              setRole(data.role);
            }
           catch (error) {
            console.error('Error fetching user details:', error);
          }
        };
    
        getUserDetails();
      }, []);

  // Function to open the menu for editing or deleting
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Function to close the options menu
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Function to handle editing of the project
  const handleEdit = async () => {
    try {
      const { id, tenantId, ...projectToUpdate } = project; // Destructure to exclude certain properties
      // Call the editProject API to update the project data
      await editProject(id, { ...projectToUpdate, tenantId, updatedAt: new Date() });
      onEdit(project); // Notify parent component of the edit
    } catch (error) {
      console.error('Error editing project: ', error); // Log error to console
    }
    handleMenuClose(); // Close the menu after editing
  };

  // Function to handle deletion of the project
  const handleDelete = async () => {
    try {
      // Call the deleteProject API to remove the project
      await deleteProjectApi(project.id, project.tenantId);
      onDelete(project.id); // Notify parent component of the deletion
    } catch (error) {
      console.error('Error deleting project: ', error); // Log error to console
    }
    handleMenuClose(); // Close the menu after deletion
  };

  // Function to navigate to the project's board page when the card is clicked
  const handleCardClick = () => {
    navigate(`/board-page/${project.id}`);
  };

  // Determine if the menu is open
  const menuOpen = Boolean(anchorEl);

  return (
    <Paper
      elevation={3} // Shadow effect
      sx={{
        position: 'relative',
        p: 1.5,
        margin: 0,
        background: 'linear-gradient(180deg, #fffef6 0%, #fbfbf2 100%)',
        color: '#17212f',
        border: '1px solid #dde3ef',
        borderRadius: 6,
        boxShadow: '0 6px 16px rgba(18, 34, 66, 0.10)', // Shadow styling
        transition: 'transform 0.3s, box-shadow 0.3s', // Animation on hover
        cursor: 'pointer',
        width: '100%',
        minHeight: '170px',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-2px)', // subtle hover motion without growing the tile
          boxShadow: '0 10px 22px rgba(18, 34, 66, 0.16)', // Enhanced shadow on hover
        },
      }}
      onClick={handleCardClick} // Click event to navigate
    >
      {/* Header section displaying the project name */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #5b7cfa 0%, #4f6fe8 55%, #4763d4 100%)',
          borderRadius: 4,
          px: 1.25,
          py: 0.75,
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.18)',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '0.92rem',
            fontWeight: 700,
            color: '#FFFFFF',
            textAlign: 'center',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {project.prjName?.trim() || 'Untitled Project'}
        </Typography>
      </Box>

      {/* Project details section */}
      <Box sx={{ mt: 1, display: 'grid', gap: 0.45, flexGrow: 1, alignContent: 'start' }}>
        {/* <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          <strong>Tenant ID:</strong> {project.tenantId}
        </Typography> */}
        <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.35 }}>
          <strong>Start Date:</strong> {project.startDate || '-'}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.35 }}>
          <strong>Due Date:</strong> {project.dueDate || '-'}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.82rem',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            cursor: 'default',
            lineHeight: 1.35,
            color: '#253142',
          }}
        >
          <strong>Description:</strong> {project.description || '-'}
        </Typography>
      </Box>

      {/* Action buttons for editing and deleting the project */}
      <CardActions sx={{ mt: 'auto', pt: 0.5, px: 0, pb: 0, justifyContent: 'flex-end', gap: 0.5 }}>
        {isAdmin && (  // Render buttons only if the user is an Admin
          <>
            <IconButton
              onClick={(e) => { e.stopPropagation(); handleEdit(); }}
              color="primary"
              size="small"
              sx={{ backgroundColor: 'rgba(37,99,235,0.08)' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              onClick={(e) => { e.stopPropagation(); handleDelete(); }} 
              size="small"
              sx={{ color: '#dc2626', backgroundColor: 'rgba(220,38,38,0.08)' }}  // Change delete icon color to red
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </CardActions>

      {/* Hover Box for additional project information */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.9)', // Semi-transparent white
          borderRadius: 8,
          padding: 2,
          display: 'none', // Initially hidden
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2,
          '&:hover': {
            display: 'flex', // Show on hover
          },
        }}
      >
        <Box sx={{ textAlign: 'left', width: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {project.prjName} {/* Display project name again */}
          </Typography>
          <Typography variant="body2">
            <strong>Due Date:</strong> {project.dueDate}
          </Typography>
          <Typography variant="body2">
            <strong>Start Date:</strong> {project.startDate}
          </Typography>
          <Typography variant="body2">
            <strong>Description:</strong> {project.description} {/* Full description displayed on hover */}
          </Typography>
        </Box>
      </Box>

      {/* Context menu for edit and delete options */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Project
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete Project
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default ProjectCard;
